/**
 * @fileoverview Application boot — phased pipeline + the `bootApp`
 *               factory that per-face entry shells call.
 * @module apps/network/global/main
 *
 * Mirrors lyn's `global/main.ts` phased structure, trimmed to what
 * network actually needs:
 *
 *   Phase 1: DETECT   (sync, <1ms)   — capability snapshot
 *                                     (frozen onto globalThis).
 *   Phase 2: GRAPH    — SKIPPED      — lyn's skill-graph
 *                                     tier-isolation preflight does
 *                                     not apply to network.
 *   Phase 3: GPU      — SKIPPED      — no GPU dependency.
 *   Phase 4: MOUNT    (sync)         — mount(App, { target: #app }).
 *   Phase 5: POST     (sync)         — performance marks, dev logs.
 *
 * Each face (admin, future consumer) brings its own entry App
 * component and calls `bootApp(App)` from its product-shell main.ts.
 * That keeps the phased boot canonical while letting each face own
 * its screen registry, env config, and root component.
 */

import { mount, type Component } from "svelte";

// ──────────────────────────────────────────────────────────────────
// Phase 1: DETECT — capability snapshot
// ──────────────────────────────────────────────────────────────────

interface NetworkCapabilities {
  readonly localStorage: boolean;
  readonly broadcastChannel: boolean;
  readonly webWorker: boolean;
  readonly fetch: boolean;
  readonly userAgent: string;
}

const capabilities: Readonly<NetworkCapabilities> = Object.freeze({
  localStorage:
    typeof globalThis !== "undefined" &&
    typeof globalThis.localStorage !== "undefined",
  broadcastChannel: typeof BroadcastChannel !== "undefined",
  webWorker: typeof Worker !== "undefined",
  fetch: typeof fetch !== "undefined",
  userAgent:
    typeof navigator !== "undefined" ? navigator.userAgent : "(server)",
});

declare global {
  // eslint-disable-next-line no-var
  var __NETWORK_CAPABILITIES: Readonly<NetworkCapabilities> | undefined;
}
globalThis.__NETWORK_CAPABILITIES = capabilities;

// ──────────────────────────────────────────────────────────────────
// Phase 2: GRAPH — preflight (SKIPPED for network)
// Phase 3: GPU   — adapter request (SKIPPED for network)
// ──────────────────────────────────────────────────────────────────
// Phases 2 + 3 are deliberate honest-scope skips. The phase
// comments stay in place so future capabilities (e.g. a
// substrate-binding validator at boot, or a topology-rendering
// GPU path) land at the obvious spot.

/**
 * Phase 4 + 5: mount the given App into `#app` (or a custom target)
 * and emit dev-mode boot diagnostics. Each face's product shell
 * (e.g. `apps/network/admin/src/main.ts`) imports this and calls
 * `bootApp(AdminApp)`.
 *
 * The App is expected to take NO required props — face-specific
 * configuration lives inside the App itself (env reads, screen
 * registry, etc.). Returns the mounted Svelte app handle.
 */
export function bootApp(
  App: Component<Record<string, never>>,
  options?: {
    /** Override the default `#app` mount target. */
    readonly target?: HTMLElement;
  },
): ReturnType<typeof mount> {
  // Phase 4 — MOUNT
  const target =
    options?.target ??
    (document.getElementById("app") as HTMLElement | null);
  if (!target) {
    throw new Error(
      "network/admin: missing #app target in index.html. " +
        "Did the document load fully before main.ts ran?",
    );
  }

  const app = mount(App, { target });

  // Phase 5 — POST
  if (import.meta.env.DEV) {
    performance.mark?.("network-admin:boot");
    // eslint-disable-next-line no-console
    console.log("[network/admin] boot complete", {
      capabilities,
      target,
    });
  }

  return app;
}
